import { useEffect } from "react";
import { useLocation } from "react-router-dom";

/**
 * <SEO />
 *
 * Per-page SEO helper. Updates <title> and the relevant meta/OG/Twitter tags
 * whenever the props change, then cleans up the JSON-LD blocks it injected.
 *
 * Strategy:
 *   - title             → <title>
 *   - description       → <meta name="description"> + og:description + twitter:description
 *   - image             → og:image + twitter:image (absolute URL; if relative we prepend cxoora.com)
 *   - keywords          → <meta name="keywords"> (lowest weight today but harmless)
 *   - jsonLd            → one or more JSON-LD <script> blocks (BreadcrumbList, FAQPage, etc.)
 *
 * The canonical URL is handled separately by <CanonicalLink /> (Phase 7).
 */

const CANONICAL_HOST = "https://cxoora.com";

const setMeta = (selector, attr, value) => {
  let el = document.head.querySelector(selector);
  if (!el) {
    el = document.createElement("meta");
    const [_, key, name] = /\[([^=]+)="?([^"\]]+)"?\]/.exec(selector) || [];
    if (key && name) el.setAttribute(key, name);
    document.head.appendChild(el);
  }
  el.setAttribute(attr, value);
};

const absoluteUrl = (urlOrPath) => {
  if (!urlOrPath) return undefined;
  if (urlOrPath.startsWith("http")) return urlOrPath;
  if (urlOrPath.startsWith("//")) return `https:${urlOrPath}`;
  return `${CANONICAL_HOST}${urlOrPath.startsWith("/") ? "" : "/"}${urlOrPath}`;
};

export const SEO = ({ title, description, image, keywords, type = "website", jsonLd }) => {
  const location = useLocation();

  useEffect(() => {
    if (typeof document === "undefined") return undefined;

    const fullTitle = title
      ? (title.includes("CXOORA") ? title : `${title} | CXOORA`)
      : "CXOORA — Fractional Manager per PMI Italiane";
    document.title = fullTitle;

    const desc = description || "CXOORA è la piattaforma B2B che mette in contatto PMI italiane e manager fractional verificati (CFO, CMO, COO, CRO). Check-up AI gratuito, matching in 48h.";
    setMeta('meta[name="description"]', "content", desc);
    if (keywords) setMeta('meta[name="keywords"]', "content", keywords);

    const url = `${CANONICAL_HOST}${location.pathname}`;
    const img = absoluteUrl(image || "/og-image.svg");

    setMeta('meta[property="og:title"]', "content", fullTitle);
    setMeta('meta[property="og:description"]', "content", desc);
    setMeta('meta[property="og:url"]', "content", url);
    setMeta('meta[property="og:image"]', "content", img);
    setMeta('meta[property="og:type"]', "content", type);

    setMeta('meta[name="twitter:title"]', "content", fullTitle);
    setMeta('meta[name="twitter:description"]', "content", desc);
    setMeta('meta[name="twitter:image"]', "content", img);

    // JSON-LD: inject per-page blocks tagged with data-seo so we can clean them up.
    const blocks = Array.isArray(jsonLd) ? jsonLd : (jsonLd ? [jsonLd] : []);
    const injected = blocks.map((data, i) => {
      const script = document.createElement("script");
      script.type = "application/ld+json";
      script.setAttribute("data-seo", "page");
      try {
        script.text = JSON.stringify(data);
      } catch (_) {
        return null;
      }
      document.head.appendChild(script);
      return script;
    }).filter(Boolean);

    return () => {
      injected.forEach((script) => {
        if (script.parentNode) script.parentNode.removeChild(script);
      });
    };
  }, [title, description, image, keywords, type, jsonLd, location.pathname]);

  return null;
};

/**
 * Helper: build a BreadcrumbList JSON-LD object.
 * Usage: jsonLd={breadcrumbList([{name: "Home", path: "/"}, {name: "Per le imprese", path: "/per-le-imprese"}])}
 */
export const breadcrumbList = (items) => ({
  "@context": "https://schema.org",
  "@type": "BreadcrumbList",
  itemListElement: items.map((it, i) => ({
    "@type": "ListItem",
    position: i + 1,
    name: it.name,
    item: it.path?.startsWith("http") ? it.path : `${CANONICAL_HOST}${it.path || "/"}`,
  })),
});

/**
 * Helper: build a FAQPage JSON-LD object.
 * Usage: jsonLd={faqPage([{q: "...", a: "..."}, ...])}
 */
export const faqPage = (faqs) => ({
  "@context": "https://schema.org",
  "@type": "FAQPage",
  mainEntity: faqs.map((f) => ({
    "@type": "Question",
    name: f.q,
    acceptedAnswer: {
      "@type": "Answer",
      text: f.a,
    },
  })),
});

export default SEO;
